import type { DateTime } from "luxon";

/**
 * A datetime field as it reaches a hook. `axiosInstance` runs every response through `convertIsoDates`, which turns an
 * ISO string into a `DateTime` in the app's default zone — but only when the string matches its regex. So a field can
 * arrive as either, and the parse step (`parseScheduleChanges.ts`) normalises both into the department's zone.
 */
export type WireDateTime = DateTime | string;

/**
 * What a change did to the schedule, in the order a picker lists them. Requests that change nothing yet (a posting, a
 * pickup or replacement ask) are not schedule changes — the Requests tab has them; an approved pickup arrives as
 * `added`. `bulk_update` is one large update that moved many shifts at once.
 */
export const SCHEDULE_CHANGE_ACTIONS = ["added", "removed", "edited", "call_off", "time_off", "swapped", "status_changed", "bulk_update"] as const;

export type ScheduleChangeAction = (typeof SCHEDULE_CHANGE_ACTIONS)[number];

/** Who made a change, relative to the person looking: themselves, another person, or the system. In picker order. */
export const SCHEDULE_CHANGE_ACTOR_KINDS = ["me", "other", "system"] as const;

export type ScheduleChangeActorKind = (typeof SCHEDULE_CHANGE_ACTOR_KINDS)[number];

/**
 * Where a change came from. `user` is an admin in the app and `staff` a staff member (the staff app or an SMS reply);
 * `agency` an agency platform; `auto_approve`, `hris` and `system` are InHouse acting on its own.
 */
export type ScheduleChangeActorSource = "auto_approve" | "hris" | "agency" | "system" | "user" | "staff";

/** `GET /schedule-changes/departments/{departmentId}/feed` — one line of the "what changed" feed. */
export type ScheduleChangeItemWire = {
	id: string;
	apiRequestId: string | null;
	changedAt: WireDateTime;
	action: ScheduleChangeAction;
	actorKind: ScheduleChangeActorKind;
	actorSource: ScheduleChangeActorSource;
	actorUserId: string | null;
	actorFirstName: string | null;
	/** For a swap: the nurse who asked to swap (the swap-out nurse). */
	nurseId: number | null;
	/** For a swap: the nurse who took the swap-out shift. Null when the swap isn't paired. */
	swapWithNurseId: number | null;
	/** For a swap: the swap-out shift — the asker's before the swap. */
	shiftStart: WireDateTime | null;
	shiftEnd: WireDateTime | null;
	jobId: string | null;
	/** How many staff the change touched — more than one when one update moved several people (`nurseId` is then null). */
	affectedNurseCount: number;
	/** Who those are when the change touched several people; empty when `nurseId` / `swapWithNurseId` name everyone. */
	affectedNurseIds: readonly number[];
};

export type ScheduleChangesFeedResponse = {
	items: ScheduleChangeItemWire[];
};

/** A feed item with every datetime in the department's timezone. */
export type ScheduleChangeItem = Omit<ScheduleChangeItemWire, "changedAt" | "shiftStart" | "shiftEnd"> & {
	changedAt: DateTime;
	shiftStart: DateTime | null;
	shiftEnd: DateTime | null;
};

/**
 * A wire record with every datetime as an ISO string — the form the query cache holds. Plain JSON, so TanStack's
 * structural sharing can see that a poll returned the same changes and hand every consumer the same object.
 */
export type ScheduleChangeItemCached = Omit<ScheduleChangeItemWire, "changedAt" | "shiftStart" | "shiftEnd"> & {
	changedAt: string;
	shiftStart: string | null;
	shiftEnd: string | null;
};

/** The feed as the Changes tab reads it: the window's changes. */
export type ScheduleChangesFeed = { items: ScheduleChangeItem[] };

/**
 * The span of time a query asks about. `until: null` means "up to now": the hook stamps the current time when the request
 * is sent, so a refetch picks up changes made since the last one without changing the cache key.
 */
export type ScheduleChangesWindow = { since: DateTime; until: DateTime | null };

/** The windows a scheduler can pick from. */
export type ScheduleChangesWindowId = "sinceLastVisit" | "last2Days" | "lastWeek";

export type ScheduleChangesWindowPreset = { id: ScheduleChangesWindowId; since: DateTime };
